'use client'

import { useEffect, useMemo, useSyncExternalStore } from 'react'

import type { RecordSourceKey, RecordSummary } from '@/helpers/records/recordSources'
import { fetchRecord, getRecordSummary, hasRecordFailed, hasRecordSource, subscribeRecords } from '@/libs/recordStore'
import type { ModuleKey } from '@/utils/constants/navigationLabels'
import { MODULE_ROUTES } from '@/utils/constants/navigationLabels'

export type RecordSummaryWithLink = RecordSummary & { href: string; loading: boolean }

// Si la petición falla se entrega un resumen vacío, para no quedar cargando ni reintentar en bucle.
const FAILED_SUMMARY: RecordSummary = {}

export const useRecordSummary = (moduleKey: ModuleKey | undefined, recordId?: string | number) => {
  const isKnown = Boolean(moduleKey && recordId && hasRecordSource(moduleKey))

  const summary = useSyncExternalStore(
    subscribeRecords,
    () => {
      if (!isKnown) return null

      return (
        getRecordSummary(moduleKey as ModuleKey, recordId as string | number) ??
        (hasRecordFailed(moduleKey as ModuleKey, recordId as string | number) ? FAILED_SUMMARY : null)
      )
    },
    () => null
  )

  useEffect(() => {
    if (isKnown && !summary) fetchRecord(moduleKey as RecordSourceKey, recordId as string | number).catch(() => {})
  }, [isKnown, summary, moduleKey, recordId])

  return useMemo<RecordSummaryWithLink>(
    () => ({
      ...summary,
      href: moduleKey ? `${MODULE_ROUTES[moduleKey]}/${recordId}` : '',
      loading: isKnown && !summary
    }),
    [summary, isKnown, moduleKey, recordId]
  )
}
